import { useCallback, useEffect, useRef, useState } from 'react'

import type { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import type { TooltipContentProps } from 'recharts'
import type { Payload } from 'recharts/types/component/DefaultTooltipContent'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'

import { BALANCE_DEMAND_SERIES_COLORS } from '~/constants/chartPalette'
import { typography } from '~/constants/typography'
import { useTooltipPlacement } from '~/hooks/useTooltipPlacement'
import { isAwaitingVoltage } from '~/models/awaitingVoltage'
import { badgeColors } from '~/models/badgeColors'
import { komaEndTime } from '~/models/komaToTime'
import { formatKwh } from '~/models/kwhDisplay'

const TOOLTIP_COLORS = BALANCE_DEMAND_SERIES_COLORS

/**
 * カーソルとツールチップの間隔。横方向は design/js/charts.js のホバーツールチップ
 * (`rect.left + cx + 16`) と同値。縦方向は design が `cy - 10` 固定 (= 常にカーソルの
 * 少し上・下端では上端 clamp のみ) なのに対し、ここでは同じ 16px を下向きに取り、
 * ウィンドウ下端で反転させる (= カーソル右下基準というユーザー製品判断)。
 */
const CURSOR_OFFSET_PX = 16

/**
 * body 直下に出すため、重なりはページ内ではなく Chakra の zIndex スケールで決める
 * (`zIndex.tooltip` = 1800)。従来の 30 のままだと、同じく body へ portal される
 * select menu (1500) やモーダルの下に潜る。
 */
const TOOLTIP_Z_INDEX = 1800

/**
 * ツールチップを載せるチャートの外枠。Recharts が content に渡す `coordinate` は
 * チャート内の相対座標なので、ウィンドウ基準で配置するにはこの矩形が要る。
 */
type ChartRef = React.RefObject<HTMLDivElement | null>

/**
 * ツールチップが読む 1 コマぶんの値。一覧 (`KomaData`) と詳細 (`DetailKomaData`) の
 * 共通部分だけを要求し、画面固有の列 (実績ステータス / 予報系列 等) は含めない。
 */
export type DemandTooltipKoma = {
  /**
   * 1 始まりのコマ番号 (1..48)。詳細は backend 応答の値、一覧は converter が
   * 開始時刻から復元した値。0 始まりの配列 index と混ぜないこと。
   */
  koma: number
  time: string
  /** 計画なし (詳細で backend が計画値を返さないコマ) では null。バッジを出さない。 */
  demandPlan: number | null
  demandActual: number | null
  demandActualCorrected: number | null
  temperature: number | null
  precipitation: number | null
  procurementNet: number | null
  demandExtraHigh: number | null
  demandHigh: number | null
  demandLow: number | null
  dewPoint: number | null
  /** 速報が未着の電圧階級 (空 = 確定)。系列ではなくコマの属性なので可視性トグルに従わない。 */
  awaitingVoltageClasses: readonly VoltageClass[]
}

/**
 * 需要チャートの系列可視性 (フィルタの 基本表示/分析補助/詳細 トグルの実効値)。
 * 親チェックボックス OFF の無効化と、内訳が総量と整合しない時の積み上げ抑止
 * (`hasVoltageBreakdown`) は呼び出し側で畳み込んでから渡す — 生値を渡すと
 * チャートが描いていない系列をツールチップだけが出す。
 */
export type DemandTooltipVisibility = {
  demandPlan: boolean
  demandActual: boolean
  /** 実績値(補完) バッジ。 */
  actualCorrected: boolean
  /** 電圧階級別 特別高圧の帯。 */
  voltageExtraHigh: boolean
  /** 電圧階級別 高圧の帯。 */
  voltageHigh: boolean
  /** 電圧階級別 低圧の帯。 */
  voltageLow: boolean
  /** 天気アイコン帯。ON の時だけヘッダーに天気アイコンを出す。 */
  weatherStrip: boolean
  temperature: boolean
  precipitation: boolean
  procurementNet: boolean
  dewPoint: boolean
}

// 送電端値は小数を伴うため表示時に四捨五入する (formatKwh の godoc 参照)。
const fmtKwh = formatKwh
const fmtTemp = (v: number): string => `${v}℃`
const fmtRain = (v: number): string => `${v}mm`

/** 色付きラベル + 値 の 1 バッジ (design renderBgChart の badge 相当)。 */
const TooltipBadge = ({
  color,
  label,
  value,
}: {
  color: string
  label: string
  value: string
}): React.ReactElement => {
  // 地色・文字色とも系列色から導く。 文字色は design (readableOn) と同じ白/黒判定で、
  // それが AA 4.5:1 に届かない系列だけ地色を同色相のまま沈める (badgeColors 参照)。
  const badge = badgeColors(color)

  return (
    <Box as="span" alignItems="center" gap="3px" display="inline-flex">
      <span
        style={{
          display: 'inline-block',
          padding: '1px 5px',
          // eslint-disable-next-line no-restricted-syntax -- BgChart tooltip badge と同系統の raw style
          borderRadius: '3px',
          background: badge.background,
          color: badge.color,
          fontSize: '11px',
          fontWeight: 700,
          lineHeight: '16px',
        }}
      >
        {label}
      </span>
      {/* fontSize (14px) + fontWeight (700) の組が役割と一致しないため raw 維持。 */}
      <span
        style={{
          fontSize: '14px',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </Box>
  )
}

/** セクション見出し + バッジ群 (2 セクション目以降は上罫線で区切る)。 */
const TooltipSection = ({
  title,
  divider,
  children,
}: {
  title: string
  divider: boolean
  children: React.ReactNode
}): React.ReactElement => (
  <Box
    marginTop={divider ? '4px' : undefined}
    paddingTop={divider ? '4px' : undefined}
    borderColor="Border.lowEmphasis"
    borderTopWidth={divider ? '1px' : undefined}
  >
    {/* section 見出し (12px/600) = 役割 captionStrong (sm/semibold)。 raw DOM なので typography の
        CSSProperties で当てる (UI 辞書 dictionary/fonts.md「サイズと太さをセットで引く」) */}
    <div
      style={{
        ...typography.captionStrong,
        color: LightModeSemanticColors.Text.midEmphasis.value,
        marginBottom: '2px',
      }}
    >
      {title}
    </div>
    <Flex alignItems="center" flexWrap="wrap" gap="8px">
      {children}
    </Flex>
  </Box>
)

/**
 * 需要チャートのホバーツールチップ (design/js/charts.js renderBgChart 相当)。
 * 一覧 (BalanceDemand の RegionChart) と詳細 (BalanceDemandDetail の DetailChart)
 * が同一構造で使う。
 *
 * コマ番号 + 時間レンジ + 天気アイコン/気温、需要 (計画/実績(補完)/実績)、
 * 電圧階級別 (値がある時のみ)、分析補助 (気温/降水量/調達-販売)、詳細 (露点)
 * を色分けバッジで表示する。値が null の系列と、トグル OFF の系列はバッジごと
 * 出さない (= セクション内が空ならセクション見出しも出さない)。
 *
 * 太陽光 (計画/実績) セクションは #5282 でデータソース未確定 (#2002) のため
 * 画面から削除した (doji-bg.md Discussion「太陽光計画値/実績値 系列の扱い」)。
 */
export const DemandTooltipContent = ({
  active,
  payload,
  coordinate,
  chartRef,
  visibility,
  weatherIcons,
  temperatureForecasts,
  gcKoma,
  currentKoma,
  t,
}: TooltipContentProps<number, string> & {
  chartRef: ChartRef
  visibility: DemandTooltipVisibility
  weatherIcons: string[]
  /**
   * 予想気温 (気象予報 gRPC 由来、コマ index 0..47)。時系列予報の正時の点のコマに
   * だけ値を持ち、他は null (AC-WD-003a)。需要の行 (`DemandTooltipKoma`) には
   * 予想気温が載らないため、天気アイコンと同じく別系列で受ける。
   */
  temperatureForecasts: ReadonlyArray<number | null>
  /** GC を迎えた最後のコマ (1 始まり)。0 = 該当なし (未来日)。 */
  gcKoma: number
  /** 進行中のコマ (1 始まり)。0 = 該当なし (当日以外)。 */
  currentKoma: number
  t: TFunction
}): React.ReactElement | null => {
  const typedPayload = payload as
    | ReadonlyArray<Payload<number, string>>
    | undefined
  // Recharts の payload は entry ごとに row を持つ想定だが、型では optional。
  // 欠けた entry で描きにいくと row.time 参照で throw し、ページを包む
  // ErrorBoundary が無いため画面ごと落ちる。表示を諦める方に倒す。
  const row = (typedPayload?.[0] as { payload?: DemandTooltipKoma } | undefined)
    ?.payload
  const isOpen = active === true && row != null

  // why not 生のマウス座標を別途追う: Recharts が渡す coordinate は
  // x = ホバー中コマの目盛り位置 / y = マウスの相対位置で、横だけコマ単位に
  // 量子化される (実測のズレは一覧 ~2px / 詳細 ~12px)。window の mousemove を
  // 自前で追えば生の座標は得られるが、ツールチップが在るのに位置が未確定な
  // フレームが生まれる (活性化した mousemove を listener 登録前に取り逃がす)。
  // ツールチップが説明している対象はコマなので、コマに揃う方が読みも合う。
  const { tooltipRef, placement } = useTooltipPlacement({
    anchorRef: chartRef,
    anchorOffsetX: coordinate?.x,
    anchorOffsetY: coordinate?.y,
    cursorOffset: CURSOR_OFFSET_PX,
    isOpen,
  })

  if (!isOpen || row == null) return null

  const komaIndex = row.koma
  const timeRange = `${row.time} – ${komaEndTime(row.time)}`
  // gcKoma / currentKoma は「該当なし」を 0 で表す (backend の option.None →
  // proto zero value)。時刻文字列で突き合わせると 0 が "00:00" に化けて
  // 未来日・過去日のコマ 1 に GC / 現在バッジが付くため、コマ番号で判定する。
  // GC バッジは「次に GC を迎えるコマ」= gcKoma + 1 に付く (同画面の GC 縦線・
  // サマリーの「次のGC コマ gcKoma+1」と同じ位置)。
  const isGc = gcKoma > 0 && komaIndex === gcKoma + 1
  const isNow = currentKoma > 0 && komaIndex === currentKoma
  // weatherIcons はコマ index 0..47 の配列 (models/weather/koma.ts)。komaIndex は
  // 1 始まりなのでそのまま添字にすると 30 分後の天気を出し、コマ 48 が空になる。
  const wIcon = visibility.weatherStrip
    ? (weatherIcons[komaIndex - 1] ?? '')
    : ''
  // 天気アイコンと同じく 0 始まり配列を 1 始まりコマ番号で引く。
  const temperatureForecast = temperatureForecasts[komaIndex - 1] ?? null
  const showTemperatureForecast =
    visibility.temperature && temperatureForecast !== null

  const hasDemand =
    (visibility.demandPlan && row.demandPlan !== null) ||
    (visibility.actualCorrected && row.demandActualCorrected !== null) ||
    (visibility.demandActual && row.demandActual !== null)

  // 階級ごとに個別 ON/OFF できる (AC-BDB-026) ため、 バッジも階級単位で出す。
  // OFF の階級は積み上げにも描かれないので、 ツールチップにも出さない。
  const showExtraHigh =
    visibility.voltageExtraHigh && row.demandExtraHigh !== null
  const showHigh = visibility.voltageHigh && row.demandHigh !== null
  const showLow = visibility.voltageLow && row.demandLow !== null
  const hasVoltage = showExtraHigh || showHigh || showLow

  return (
    <Box
      ref={tooltipRef}
      minWidth="200px"
      padding="10px 14px"
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      /* 影は DS の `shadLg`。 生の rgba 直書きは design の `--shadow-lg` とも
         DS とも別物で、 3 チャートが同じ値を各自で持っていた (#7064)。 */
      boxShadow="shadLg"
      style={{
        // why not left / top を指定する: 幅指定の無い要素は「ウィンドウ幅 - left」に
        // 折り返し幅が縮むため、右寄りほどツールチップが細くなり
        // 実寸測定 → 配置 → 再折り返し → 再測定 が振動する。原点 (wrapper 側で
        // viewport 左上に固定済み) に置いたまま transform で動かせば幅は一定。
        transform:
          placement == null
            ? undefined
            : `translate(${placement.left}px, ${placement.top}px)`,
        // 実寸を測るまで配置が決まらない 1 フレームを隠す。visibility ではなく
        // opacity を使うのは、非表示のまま実寸を測れるようにするため。
        opacity: placement == null ? 0 : 1,
      }}
    >
      {/* Header */}
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        gap="20px"
        marginBottom="6px"
      >
        <Flex alignItems="center" gap="4px">
          {/* fontSize (15px) + fontWeight (700) の組が役割と一致しないため raw 維持。 */}
          <span style={{ fontSize: '15px', fontWeight: 700 }}>
            {t('common:demandChartTooltip.koma')} {komaIndex}
          </span>
          {isNow && (
            <TooltipBadge
              color={LightModeSemanticColors.Surface.accentPrimary.value}
              label={t('common:demandChartTooltip.now')}
              value=""
            />
          )}
          {isGc && (
            <TooltipBadge
              /* GC の赤は **`Surface.negativeHigh`** (#d80004)。 `Text.negativeHigh`
                 (#870103) は暗いマルーンで、 design の GC 色
                 (`js/charts.js:928` `cssVar('surface-negative-high')`) と別物。
                 同じチャートの GC 縦線・ラベルは既に #d80004 なので、 バッジだけ
                 取り残されて **1 画面に 2 つの GC 色**が併存していた (#7064)。 */
              color={LightModeSemanticColors.Surface.negativeHigh.value}
              label={t('common:demandChartTooltip.gc')}
              value=""
            />
          )}
        </Flex>
        <Box textAlign="right">
          <Text as="div" color="Text.midEmphasis" fontSize="xs">
            {timeRange}
          </Text>
          {(wIcon !== '' ||
            (visibility.temperature && row.temperature !== null)) && (
            <Text as="div" color="Text.midEmphasis" fontSize="xs">
              {wIcon !== '' ? `${wIcon} ` : ''}
              {visibility.temperature && row.temperature !== null
                ? fmtTemp(row.temperature)
                : ''}
            </Text>
          )}
        </Box>
      </Flex>

      {/* 低圧未着のコマの印。実績値(補完) とは別軸なので、補完済みの
          コマでも併記する。 見た目は design `js/charts.js` の 1 行 (時刻範囲と
          同じ控えめな文字。 待ちの状態なので強調色は付けない)。 */}
      {isAwaitingVoltage(row.awaitingVoltageClasses) && (
        <Text
          as="div"
          marginBottom="4px"
          color="Text.midEmphasis"
          fontSize="xs"
        >
          {t('page:balanceDemand.awaitingVoltage.chartTooltipLine')}
        </Text>
      )}

      {/* 需要 (トグル OFF の系列はバッジも出さない)。
          電圧階級で絞り込み中は計画値が畳み込みで落ちるため、実績も OFF / 未着なら
          3 バッジすべてが消える。他セクションと同じく、その時は見出しも出さない。 */}
      {hasDemand && (
        <TooltipSection
          divider={false}
          title={t('common:demandChartTooltip.demand')}
        >
          {visibility.demandPlan && row.demandPlan !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.demandPlan}
              label={t('common:demandChartTooltip.plan')}
              value={fmtKwh(row.demandPlan)}
            />
          )}
          {visibility.actualCorrected && row.demandActualCorrected !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.demandActualCorrected}
              label={t('common:demandChartTooltip.actualCorrected')}
              value={fmtKwh(row.demandActualCorrected)}
            />
          )}
          {visibility.demandActual && row.demandActual !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.demandActual}
              label={t('common:demandChartTooltip.actual')}
              value={fmtKwh(row.demandActual)}
            />
          )}
        </TooltipSection>
      )}

      {/* 電圧階級別 (トグル ON かつ値がある階級のみ) */}
      {hasVoltage && (
        <TooltipSection divider title={t('common:demandChartTooltip.voltage')}>
          {visibility.voltageExtraHigh && row.demandExtraHigh !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.voltageExtraHigh}
              label={t('common:demandChartTooltip.extraHigh')}
              value={fmtKwh(row.demandExtraHigh)}
            />
          )}
          {visibility.voltageHigh && row.demandHigh !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.voltageHigh}
              label={t('common:demandChartTooltip.high')}
              value={fmtKwh(row.demandHigh)}
            />
          )}
          {visibility.voltageLow && row.demandLow !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.voltageLow}
              label={t('common:demandChartTooltip.low')}
              value={fmtKwh(row.demandLow)}
            />
          )}
        </TooltipSection>
      )}

      {/* 分析補助 */}
      {((visibility.temperature && row.temperature !== null) ||
        showTemperatureForecast ||
        (visibility.precipitation && row.precipitation !== null) ||
        (visibility.procurementNet && row.procurementNet !== null)) && (
        <TooltipSection divider title={t('common:demandChartTooltip.analysis')}>
          {visibility.temperature && row.temperature !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.temperature}
              label={t('common:demandChartTooltip.temperature')}
              value={fmtTemp(row.temperature)}
            />
          )}
          {visibility.temperature && temperatureForecast !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.temperature}
              label={t('common:demandChartTooltip.temperatureForecast')}
              value={fmtTemp(temperatureForecast)}
            />
          )}
          {visibility.precipitation && row.precipitation !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.precipitation}
              label={t('common:demandChartTooltip.precipitation')}
              value={fmtRain(row.precipitation)}
            />
          )}
          {visibility.procurementNet && row.procurementNet !== null && (
            <TooltipBadge
              color={TOOLTIP_COLORS.procurementNet}
              label={t('common:demandChartTooltip.procurementNet')}
              value={fmtKwh(row.procurementNet)}
            />
          )}
        </TooltipSection>
      )}

      {/* 詳細 (露点)。backend は #2002 まで露点を nil で返すため、他系列と同じく
          null ガードして「実在する 0℃」と欠測を取り違えない (下段の気象チャートは
          実測露点を別途描く)。 */}
      {visibility.dewPoint && row.dewPoint !== null && (
        <TooltipSection divider title={t('common:demandChartTooltip.detail')}>
          <TooltipBadge
            color={TOOLTIP_COLORS.dewPoint}
            label={t('common:demandChartTooltip.dewPoint')}
            value={fmtTemp(row.dewPoint)}
          />
        </TooltipSection>
      )}
    </Box>
  )
}

/**
 * Recharts の `<Tooltip>` へそのまま展開する props と、チャートに付ける外枠 ref。
 * `chartRef` は `<Tooltip>` の props ではないので、呼び出し側で分割してから展開する。
 */
type DemandTooltipProps = {
  /** `<ResponsiveContainer>` を包む要素に付ける (= 配置計算の原点)。 */
  chartRef: ChartRef
  /** 相対座標が古い間だけ false (= Recharts に閉じさせる)。他は Recharts 任せ。 */
  active: false | undefined
  cursor: { fill: string }
  portal: HTMLElement
  content: (props: TooltipContentProps) => React.ReactElement | null
  isAnimationActive: false
  wrapperStyle: React.CSSProperties
}

/**
 * スクロール・リサイズでチャートが動いた事実を掴む。
 *
 * これらはポインタを動かさずに anchor だけを動かすので、Recharts が持つ
 * 「チャート内の相対座標」が古くなる。古い相対座標を新しい矩形と合成すると
 * 実カーソルからスクロール量ぶんずれた位置に出るし、放置すれば元の viewport
 * 座標に取り残されて無関係なカードの上に別コマの値が乗る。どちらも嘘なので、
 * 座標が確かな間だけ表示する。
 *
 * why not ツールチップの中身だけ隠す: Recharts のカーソル帯は content と独立に
 * 出続けるため、「帯は出ているのに数値が出ない」半端な状態になる (実測済み)。
 * `active: false` で Recharts ごと閉じれば帯も一緒に消える。
 *
 * 代償: 判定がチャート単位の state なので、スクロール開始時に画面上のチャートが
 * 1 度ずつ再レンダーされる (一覧は 10 枚)。連続スクロール中は同値 setState で
 * 止まるため増えない。ホバー中のチャートだけに絞る余地はあるが、開閉状態は
 * Recharts 側が持つので、絞るには content からの通知経路が要る。
 */
function useStaleWhileChartMoves(chartRef: ChartRef): boolean {
  const [isStale, setIsStale] = useState(false)

  useEffect(() => {
    const markStaleIfChartMoved = (event: Event): void => {
      const chart = chartRef.current
      // チャートを含まない領域のスクロールではチャートは 1px も動かないので
      // 閉じる理由がない (詳細画面の左ペイン = コマ表は独立にスクロールする)。
      // ページ全体のスクロールは target が document になり、これも contains で通る。
      if (chart == null) return
      const target = event.target
      if (!(target instanceof Node) || !target.contains(chart)) return
      setIsStale(true)
    }
    const markStale = (): void => setIsStale(true)

    // capture: true は内側のスクロールコンテナ (一覧のカード grid) を拾うため
    // (scroll は bubble しない)。
    window.addEventListener('scroll', markStaleIfChartMoved, {
      capture: true,
      passive: true,
    })
    // リサイズは常にチャート矩形を動かしうるので絞らない。
    window.addEventListener('resize', markStale)
    return () => {
      window.removeEventListener('scroll', markStaleIfChartMoved, {
        capture: true,
      })
      window.removeEventListener('resize', markStale)
    }
  }, [chartRef])

  useEffect(() => {
    if (!isStale) return
    const clear = (): void => setIsStale(false)

    // 復帰の合図はすべてチャート自身のイベントに限る。
    // why not window で拾う: チャート外のポインタ移動や、フィルタ入力等の無関係な
    // 打鍵まで解除の合図になる。それらでは Recharts が相対座標を取り直さないため、
    // 古い座標のまま表示を戻すことになる (= 本 PR で潰したずれの再導入)。
    // pointermove だけでなく keydown / focusin も見るのは、accessibilityLayer 経由
    // (Tab + 矢印) の利用者が pointermove を起こさないため。
    const chart = chartRef.current
    chart?.addEventListener('pointermove', clear)
    chart?.addEventListener('keydown', clear)
    chart?.addEventListener('focusin', clear)

    return () => {
      chart?.removeEventListener('pointermove', clear)
      chart?.removeEventListener('keydown', clear)
      chart?.removeEventListener('focusin', clear)
    }
  }, [chartRef, isStale])

  return isStale
}

/**
 * 需要チャートの `<Tooltip>` props を組み立てる (一覧 RegionChart / 詳細
 * DetailChart 共通)。`<Tooltip>` 自体はラップしない — Recharts は子要素を
 * component 型で走査するため、独自 wrapper に包むと tooltip として認識されない。
 *
 * 配置はカーソルの右下を既定とし、ウィンドウから溢れる軸だけを反転する
 * ([[computeTooltipPlacement]])。この配置ポリシーはユーザーの製品判断
 * (2026-08-05) — 当日需給監視担当はホバー中もグラフ全体の形を見て判断するため、
 * ツールチップがカーソルの上や中央に被ってはならない。
 *
 * why not Recharts 既定の viewBox 基準フリップ: 一覧のカードはプロットが ~150px
 * しかなく、~350px のツールチップをプロット内へクランプさせるとホバー中のグラフを
 * 丸ごと覆う (#4717 で「スクロールコンテナで見切れるよりマシ」として受け入れた
 * 妥協)。見切れ判定をウィンドウ基準に移し、body 直下へ portal してクリップ自体を
 * 無くせば両方成立する (design/js/charts.js:746 の "Fixed tooltip (body-level,
 * avoids overflow:hidden)" と同じ判断)。
 *
 * ref を引数で受けず hook 側で作るのは、「どの要素が原点か」の契約をここ 1 箇所に
 * 閉じるため。呼び出し側に作らせると、別の要素 (カード全体等) に付けても型検査を
 * 通り、ヘッダー高さぶんずれて出るだけの無言の不整合になる。
 *
 * 行型が [[DemandTooltipKoma]] を満たすことは、呼び出し側が chart へ渡す data に
 * `satisfies DemandTooltipKoma[]` を付けて compile time に固定する (Recharts の
 * payload は型情報を運ばないため、静的な接点はそこだけになる)。
 */
export function useDemandTooltipProps(args: {
  visibility: DemandTooltipVisibility
  weatherIcons: string[]
  /** 予想気温 (コマ index 0..47、正時の点以外は null)。予報が無いコマは null か範囲外。 */
  temperatureForecasts: ReadonlyArray<number | null>
  /** GC を迎えた最後のコマ (1 始まり)。0 = 該当なし。 */
  gcKoma: number
  /** 進行中のコマ (1 始まり)。0 = 該当なし。 */
  currentKoma: number
}): DemandTooltipProps {
  const {
    visibility,
    weatherIcons,
    temperatureForecasts,
    gcKoma,
    currentKoma,
  } = args
  const { t } = useTranslation()
  const chartRef = useRef<HTMLDivElement>(null)
  const isStale = useStaleWhileChartMoves(chartRef)

  const content = useCallback(
    (props: TooltipContentProps) => (
      <DemandTooltipContent
        {...(props as TooltipContentProps<number, string>)}
        visibility={visibility}
        chartRef={chartRef}
        currentKoma={currentKoma}
        gcKoma={gcKoma}
        t={t}
        temperatureForecasts={temperatureForecasts}
        weatherIcons={weatherIcons}
      />
    ),
    [
      chartRef,
      currentKoma,
      gcKoma,
      t,
      temperatureForecasts,
      visibility,
      weatherIcons,
    ],
  )

  return {
    chartRef,
    active: isStale ? false : undefined,
    cursor: { fill: 'rgba(0,0,0,0.06)' },
    portal: document.body,
    content,
    isAnimationActive: false,
    wrapperStyle: {
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: TOOLTIP_Z_INDEX,
      // portal を明示すると Recharts は wrapper に位置スタイルを一切付けない
      // (TooltipBoundingBox の hasPortalFromProps 分岐)。既定で入っていた
      // pointerEvents:none も落ちるため、ここで戻す — 無いとツールチップ自身が
      // ホバーを奪ってチャートから外れた判定になり、表示が明滅する。
      pointerEvents: 'none',
    },
  }
}
